import React, { useState } from "react";
import { Box, Typography, InputBase, Chip, CircularProgress, makeStyles } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import MemoryIcon from "@material-ui/icons/Memory";
import { dtcBadge } from "./severity";
import { useVehicleDtc } from "./useVehicleDtc";
import { Vehicle } from "./types";

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#f9fafb" },
  header: { backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb", padding: "16px 24px", flexShrink: 0 },
  titleBlock: { marginBottom: 12 },
  title: { fontWeight: 600, color: "#111827" },
  subtitle: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
    maxWidth: 360,
  },
  searchIcon: { fontSize: 16, color: "#9ca3af" },
  searchInput: { fontSize: 13, flex: 1 },
  list: { flex: 1, overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 },
  loadingRow: { display: "flex", justifyContent: "center", padding: "32px 0" },
  emptyMessage: { fontSize: 12, color: "#9ca3af", textAlign: "center", marginTop: 16 },
  card: { backgroundColor: "#fff", borderRadius: 12, border: "1px solid #f3f4f6", boxShadow: theme.shadows[1], overflow: "hidden" },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid #f3f4f6",
  },
  cardHeaderLeft: { display: "flex", alignItems: "center", gap: 12 },
  cardIcon: { fontSize: 16, color: "#9ca3af" },
  plate: { fontWeight: 600, fontSize: 13, color: "#111827" },
  makeModel: { fontSize: 12, color: "#9ca3af", marginLeft: 8 },
  countChip: { backgroundColor: dtcBadge.bg, color: dtcBadge.color, border: `1px solid ${dtcBadge.border}`, fontWeight: 500 },
  dtcRow: { display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px" },
  dtcRowBordered: { borderTop: "1px solid #f9fafb" },
  dot: { width: 8, height: 8, borderRadius: "50%", backgroundColor: "#9ca3af", flexShrink: 0, marginTop: 6 },
  dtcBody: { flex: 1, minWidth: 0 },
  codeRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  code: { fontFamily: "monospace", fontWeight: 700, fontSize: 12, color: "#111827" },
  systemChip: { height: 18, fontSize: 10, backgroundColor: "#f3f4f6", color: "#6b7280" },
  description: { fontSize: 12, color: "#4b5563", marginTop: 4 },
  detected: { fontSize: 10, color: "#9ca3af", marginTop: 4 },
}));

interface DTCPageProps {
  vehicles: Vehicle[];
  loading: boolean;
}

export function DTCPage({ vehicles, loading }: DTCPageProps) {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const trackedVehicles = vehicles.filter((v) => !!v.telemetryDeviceId);
  const { dtcByEsn, loading: dtcLoading } = useVehicleDtc(trackedVehicles.map((v) => v.telemetryDeviceId));

  // Only vehicles that actually have DTCs in the window are shown — no "No DTCs" filler rows.
  const withDtcs = trackedVehicles.filter((v) => (dtcByEsn[v.telemetryDeviceId]?.length ?? 0) > 0);
  const filtered = withDtcs.filter(
    (v) => v.plate.toLowerCase().includes(search.toLowerCase()) || v.make.toLowerCase().includes(search.toLowerCase())
  );
  const totalDTCs = trackedVehicles.reduce((n, v) => n + (dtcByEsn[v.telemetryDeviceId]?.length ?? 0), 0);

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleBlock}>
          <Typography className={classes.title}>DTC Alerts</Typography>
          <Typography className={classes.subtitle}>
            {totalDTCs} diagnostic code{totalDTCs === 1 ? "" : "s"} in the last 24 hours · vehicles without a telemetry device aren't shown
          </Typography>
        </Box>
        <Box className={classes.searchBox}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase placeholder="Search vehicle…" value={search} onChange={(e) => setSearch(e.target.value)} className={classes.searchInput} />
        </Box>
      </Box>

      <Box className={classes.list}>
        {(loading || dtcLoading) && (
          <Box className={classes.loadingRow}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && !dtcLoading && filtered.length === 0 && (
          <Typography className={classes.emptyMessage}>
            {trackedVehicles.length === 0 ? "No vehicles with a telemetry device found." : "No DTC alerts found."}
          </Typography>
        )}

        {filtered.map((v) => {
          const dtcs = dtcByEsn[v.telemetryDeviceId] ?? [];
          return (
            <Box key={v.id} className={classes.card}>
              <Box className={classes.cardHeader}>
                <Box className={classes.cardHeaderLeft}>
                  <MemoryIcon className={classes.cardIcon} />
                  <Box>
                    <Typography component="span" className={classes.plate}>{v.plate}</Typography>
                    <Typography component="span" className={classes.makeModel}>{v.make} {v.model}</Typography>
                  </Box>
                </Box>
                <Chip label={`${dtcs.length} code${dtcs.length > 1 ? "s" : ""}`} size="small" className={classes.countChip} />
              </Box>
              {dtcs.map((d, i) => (
                <Box key={`${d.code}-${i}`} className={`${classes.dtcRow} ${i > 0 ? classes.dtcRowBordered : ""}`}>
                  <Box className={classes.dot} />
                  <Box className={classes.dtcBody}>
                    <Box className={classes.codeRow}>
                      <Typography className={classes.code}>{d.code}</Typography>
                      <Chip label={d.system} size="small" className={classes.systemChip} />
                    </Box>
                    <Typography className={classes.description}>{d.description}</Typography>
                    <Typography className={classes.detected}>
                      Detected: {d.detectedAt}{d.location ? ` · ${d.location}` : ""}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
